import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
const TURNSTILE_SITE_KEY = '0x4AAAAAAB4fxEq8fhcmxW8D'; // <-- replace with your real Site Key

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    service: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  // Hook Turnstile’s auto-render callbacks (script already added in index.html)
  useEffect(() => {
    window.onTurnstileSuccess = (token) => setCaptchaToken(token || '');
    window.onTurnstileError = () => setCaptchaToken('');
    window.onTurnstileExpired = () => setCaptchaToken('');
    return () => {
      delete window.onTurnstileSuccess;
      delete window.onTurnstileError;
      delete window.onTurnstileExpired;
    };
  }, []);  

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  try {
    if (!captchaToken) {
      alert("Please complete the CAPTCHA first.");
      return;
    }

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      company: formData.company.trim(),
      service: formData.service,
      message: formData.message.trim(),
      CaptchaToken: captchaToken, // server expects this
    };

    const res = await fetch("/api/send-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) {
      throw new Error(json?.error || `Request failed: ${res.status}`);
    }

    setIsSubmitted(true);
    setFormData({ name: "", email: "", company: "", message: "", service: "" });
  } catch (err) {
    console.error("Error submitting form:", err);
    alert("Sorry, we couldn’t send your message. Please try again.");
  } finally {
    setIsSubmitting(false);
    try { window.turnstile?.reset(); } catch {}
    setCaptchaToken("");
  }
};


  if (isSubmitted) {
    return (
      <section id="contact" className="py-24 bg-gray-950 text-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <div className="text-center">
            <div className="bg-black/50 border border-white/20 p-16" style={{ borderRadius: '0px' }}>
              <CheckCircle className="text-red-600 w-16 h-16 mx-auto mb-6" />
              <h2 className="text-4xl font-bold text-white mb-4">Message Sent Successfully!</h2>
              <p className="text-white/70 text-lg mb-8">
                Thank you for reaching out. Our team will contact you within 24 hours to discuss your technology transformation needs.
              </p>
              <button 
                onClick={() => setIsSubmitted(false)}
                className="bg-red-600 text-black px-8 py-4 text-lg font-medium transition-all duration-400 hover:bg-white hover:text-black border-0"
                style={{ borderRadius: '0px' }}
              >
                Send Another Message
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-24 bg-gray-950 text-white">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl lg:text-6xl font-bold mb-6">Show us the process that is getting in the way</h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Tell us who does the work, where it gets stuck and what should become easier to see, control or complete.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-black/50 border border-white/20 p-8" style={{ borderRadius: '0px' }}>
              <h3 className="text-3xl font-semibold text-white mb-8">Get In Touch</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Mail className="text-red-600 w-6 h-6 mt-1" />
                  <div>
                    <h4 className="text-white font-medium mb-1">Email</h4>
                    <p className="text-white/70">kyi@drishtitech.in</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <Phone className="text-red-600 w-6 h-6 mt-1" />
                  <div>
                    <h4 className="text-white font-medium mb-1">Phone</h4>
                    <p className="text-white/70">+91-98-112-55534</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <MapPin className="text-red-600 w-6 h-6 mt-1" />
                  <div>
                    <h4 className="text-white font-medium mb-1">Location</h4>
                    <p className="text-white/70">Gurgaon, India</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/20">
                <h4 className="text-white font-medium mb-4">What you can bring to the conversation</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-red-600 rounded-full"></div>
                    <span className="text-white/80 text-sm">A manual or fragmented workflow</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-red-600 rounded-full"></div>
                    <span className="text-white/80 text-sm">An ERP or system integration gap</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-red-600 rounded-full"></div>
                    <span className="text-white/80 text-sm">A mobile, audit or evidence process</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-red-600 rounded-full"></div>
                    <span className="text-white/80 text-sm">A governed enterprise AI use case</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-black/50 border border-white/20 p-8" style={{ borderRadius: '0px' }}>
            <h3 className="text-3xl font-semibold text-white mb-8">Start the conversation</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-white font-medium mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/20 text-white px-4 py-3 focus:border-red-600 focus:outline-none transition-colors duration-300"
                  style={{ borderRadius: '0px' }}
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-white font-medium mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/20 text-white px-4 py-3 focus:border-red-600 focus:outline-none transition-colors duration-300"
                  style={{ borderRadius: '0px' }}
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-white font-medium mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/20 text-white px-4 py-3 focus:border-red-600 focus:outline-none transition-colors duration-300"
                  style={{ borderRadius: '0px' }}
                  placeholder="Enter your company name"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-white font-medium mb-2">
                  What best describes this?
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className={`contact-select w-full bg-black border border-white/20 px-4 py-3 focus:border-red-600 focus:ring-1 focus:ring-red-600 focus:outline-none appearance-none ${formData.service ? 'text-red-600' : 'text-white'}`}
                  style={{ borderRadius: '0px' }}
                >
                  <option value="">Select an area</option><option value="workflow">Workflow / business application</option><option value="mobile">Mobile / field application</option><option value="integration">Integration / ERP gap</option><option value="audit">Audit / compliance workflow</option><option value="modernisation">Application modernisation</option><option value="private-ai">Private AI</option><option value="advisory">Advisory</option><option value="training">Training</option><option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-white font-medium mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full bg-white/5 border border-white/20 text-white px-4 py-3 focus:border-red-600 focus:outline-none transition-colors duration-300"
                  style={{ borderRadius: '0px' }}
                  placeholder="What are you trying to fix? Who uses the process, and where does it get stuck?"
                />
              </div>
              <div
                className="cf-turnstile"
                data-sitekey={TURNSTILE_SITE_KEY}
                data-callback="onTurnstileSuccess"
                data-error-callback="onTurnstileError"
                data-expired-callback="onTurnstileExpired"
              />
              <button
                type="submit"
                disabled={isSubmitting || !captchaToken}
                className={`w-full flex items-center justify-center gap-3 px-8 py-4 text-lg font-medium transition-all duration-400 border-0 ${
                  isSubmitting 
                    ? 'bg-gray-600 text-gray-300 cursor-not-allowed' 
                    : 'bg-red-600 text-black hover:bg-white hover:text-black'
                }`}
                style={{ borderRadius: '0px' }}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Sending Message...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Start the Conversation
                  </>
                )}
              </button>
              <p className="text-white/50 text-sm">Your details will be used only to respond to this enquiry.</p></form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
